import { Question } from '@protos/content_management/content.db_pb';
import {
  LiveQuizParticipantAnswerReveal,
  LiveQuizQuestionSession,
  LiveQuizSession,
  MetricsModel,
  SessionStatusEnum,
} from '@protos/learning_management/lms.db_pb';
import { createSlice } from '@reduxjs/toolkit';

export interface ILiveQuizClassStats {
  answeredCount: number;
  correctCount: number;
  incorrectCount: number;
  unansweredCount: number;
}

export interface ILiveQuizStudentState {
  // The joinable sessions shown on Home's inline Live Quiz section — usually
  // zero or one, but a student can be enrolled in more than one section
  // (different subjects), each independently startable by its own teacher.
  live_quiz_sessions: LiveQuizSession[];
  // The ONE session the student has actually joined and is now inside
  // (Waiting Room / Play) — distinct from the discovery list above.
  live_quiz_session?: LiveQuizSession;
  firestore_custom_token?: string;
  session_status?: SessionStatusEnum;
  current_question?: LiveQuizQuestionSession;
  questions: Question[];
  has_answered_current: boolean;
  revealed_result?: LiveQuizParticipantAnswerReveal;
  joined_count?: number;
  last_question_stats?: ILiveQuizClassStats;
  // The student's own final score, present only once session_status is
  // SESSION_STATUS_COMPLETED — what the "session ended" popup shows.
  final_metrics?: MetricsModel;
}

const initialState: ILiveQuizStudentState = {
  live_quiz_sessions: [],
  questions: [],
  has_answered_current: false,
};

export const liveQuizSlice = createSlice({
  name: 'liveQuiz',
  initialState,
  reducers: {
    setLiveQuizSessions: (state, action: { payload: LiveQuizSession[] }) => {
      state.live_quiz_sessions = action.payload;
    },
    setLiveQuizSession: (state, action: { payload: LiveQuizSession | undefined }) => {
      state.live_quiz_session = action.payload;
    },
    setFirestoreCustomToken: (state, action: { payload: string | undefined }) => {
      state.firestore_custom_token = action.payload;
    },
    setSessionStatus: (state, action: { payload: SessionStatusEnum | undefined }) => {
      state.session_status = action.payload;
    },
    setCurrentQuestion: (state, action: { payload: LiveQuizQuestionSession | undefined }) => {
      state.current_question = action.payload;
      state.has_answered_current = false;
      state.revealed_result = undefined;
    },
    setLiveQuizQuestions: (state, action: { payload: Question[] }) => {
      state.questions = action.payload;
    },
    setHasAnsweredCurrent: (state, action: { payload: boolean }) => {
      state.has_answered_current = action.payload;
    },
    setRevealedResult: (
      state,
      action: { payload: LiveQuizParticipantAnswerReveal | undefined },
    ) => {
      state.revealed_result = action.payload;
    },
    setLiveQuizJoinedCount: (state, action: { payload: number | undefined }) => {
      state.joined_count = action.payload;
    },
    setLastQuestionStats: (state, action: { payload: ILiveQuizClassStats | undefined }) => {
      state.last_question_stats = action.payload;
    },
    setFinalMetrics: (state, action: { payload: MetricsModel | undefined }) => {
      state.final_metrics = action.payload;
    },
    resetLiveQuiz: () => initialState,
  },
});

export const {
  setLiveQuizSessions,
  setLiveQuizSession,
  setFirestoreCustomToken,
  setSessionStatus,
  setCurrentQuestion,
  setLiveQuizQuestions,
  setHasAnsweredCurrent,
  setRevealedResult,
  setLiveQuizJoinedCount,
  setLastQuestionStats,
  setFinalMetrics,
  resetLiveQuiz,
} = liveQuizSlice.actions;

export default liveQuizSlice.reducer;
